import {Component, DestroyRef, inject, Injector, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {combineLatest} from 'rxjs';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {ConsignmentType} from '|api/commons';
import {OwnDocumentDto} from '|api/document';
import {
  CONSIGNMENT_DIALOG_SERVICE,
  ConsignmentDialogType,
  DocumentDetailService,
  OwnConsignmentsSearchService,
  OwnConsignmentTableComponent,
  OwnConsignmentTableView
} from '|shared';
import {ApiAuthorizationService, AuthorizedEntityType, DocumentAuthorizedOperation} from '|api/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AlertComponent, LoadingIndicatorService} from '@icz/angular-essentials';
import {FilterOperator} from '@icz/angular-table';


@Component({
  selector: 'icz-document-detail-own-consignments',
  templateUrl: './document-detail-own-consignments.component.html',
  styleUrls: ['./document-detail-own-consignments.component.scss'],
  imports: [OwnConsignmentTableComponent, AlertComponent]
})
export class DocumentDetailOwnConsignmentsComponent implements OnInit {

  protected loadingIndicatorService = inject(LoadingIndicatorService);
  private activatedRoute = inject(ActivatedRoute);
  private consignmentsSearchService = inject(OwnConsignmentsSearchService);
  private documentDetailService = inject(DocumentDetailService);
  private consignmentDialogService = inject(CONSIGNMENT_DIALOG_SERVICE);
  private apiAuthorizationService = inject(ApiAuthorizationService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  @ViewChild(OwnConsignmentTableComponent)
  ownConsignmentTable!: OwnConsignmentTableComponent;

  readonly tableView = OwnConsignmentTableView.OFFICER_CREATE_CONSIGNMENTS;
  document!: OwnDocumentDto;

  readonly ConsignmentType = ConsignmentType;

  canShowOwnConsignments = true;
  showOwnConsignmentsDenied = false;

  ngOnInit() {
    this.documentDetailService.object$.pipe(
      filter(Boolean),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(document => {
      this.document = document as unknown as OwnDocumentDto;
      if (this.document.id) {
        this.apiAuthorizationService.authorizationAuthorizeDocumentOperations({
          body: {
            authorizedEntityId: this.document.id,
            authorizedEntityType: AuthorizedEntityType.DOCUMENT,
            operationsToAuthorize: [DocumentAuthorizedOperation.DOCUMENT_SHOW_OWN_CONSIGNMENTS],
          }
        }).subscribe(result => {
          this.canShowOwnConsignments = result.authorizedOperations[0].userAuthorized;
          this.showOwnConsignmentsDenied = !this.canShowOwnConsignments;
        });
      }
    });

    combineLatest([
      this.activatedRoute.queryParams,
      this.documentDetailService.object$
    ]).pipe(
      distinctUntilChanged((prev, curr) => {
        return prev[0].consignmentId === curr[0].consignmentId;
      }),
      map(([queryParams, _]) => queryParams),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(queryParams => {
      if (queryParams.consignmentId) {
        this.consignmentsSearchService.findOwnDocumentOwnConsignments(this.document.id, {
          filter: [
            {
              fieldName: 'id',
              operator: FilterOperator.equals,
              value: queryParams.consignmentId,
            }
          ]
        }).subscribe(consignmentsPage => {
          if (consignmentsPage.content.length) {
            const consignment = consignmentsPage.content[0];
            this.consignmentDialogService.openConsignmentDialog(
              this.injector,
              {
                type: ConsignmentDialogType.EDIT_CONSIGNMENT,
                documentData: {
                  id: this.document.id,
                  issdId: this.document.issdId,
                },
                preselectedConsignees: null,
                consignment,
              },
            ).subscribe(_ => {
              this.ownConsignmentTable.dataSource.reload(true);
            });
          }
        });
      }
    });
  }

}
